import React, { useEffect, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { ApiService } from '../api/ApiService';
import LoadDetailHeader, { useTransparentHeaderContrast } from '../components/LoadDetailHeader';
import { AccidentsIcon, PhoneIcon } from '../components/icons';

const PHOTO_CHECKLIST = [
  'A photo of your truck number',
  'The accident scene from multiple angles (360 degrees if possible)',
  'Specific damage to vehicles and property',
  'Skid marks, gouge marks, and debris field(s)',
  'Any signs or traffic control devices',
];

const WARNINGS = [
  'Failure to report an accident immediately is grounds for termination.',
  'Failure to report a citation issued as a result of an accident immediately is grounds for termination.',
  "Any DOT post-accident drug and alcohol test which is missed due to a driver's failure to report or reporting late will be considered a refusal.",
];

const AccidentInfo: React.FC = () => {
  const history = useHistory();
  const { onSheet, onIonScroll } = useTransparentHeaderContrast();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const handleCall911 = () => {
    ApiService.sendEventCommand('accident_Hotline', 'eventCheck', 'contact-911');
  };

  const handleCallHotline = () => {
    ApiService.sendEventCommand('accident_Hotline', 'eventCheck', 'contact-AccidentHotline');
  };

  const goBack = () => {
    if (history.length > 1) history.goBack();
    else history.replace('/docs');
  };

  return (
    <IonPage>
      <LoadDetailHeader title="Accident Info" onBack={goBack} onSheet={onSheet} />

      <IonContent
        fullscreen
        className="design-a-content design-a-content--light"
        scrollEvents={true}
        onIonScroll={onIonScroll}
      >
        <div className={`design-a design-a--detail${mounted ? ' design-a--mounted' : ''}`}>
          <div className="home-hero safety-hero accident-hero">
            <div className="safety-hero__badge" aria-hidden="true">
              <AccidentsIcon color="#ffffff" size={28} />
            </div>
            <div className="safety-hero__eyebrow">Emergency guidance</div>
            <div className="safety-hero__title home-display">Accident Info</div>
            <div className="safety-hero__meta accident-hero__meta">
              Report all accidents immediately
            </div>
          </div>

          <div className="home-sheet safety-sheet">
            <div className="accident-alert">
              <strong>All accidents must be reported immediately</strong>
              <p>Follow the steps below in order.</p>
            </div>

            <section className="safety-section">
              <h2 className="safety-section__title home-section-label">Immediate actions</h2>

              <div className="accident-step">
                <div className="accident-step__number">1</div>
                <div className="accident-step__body">
                  <div className="accident-step__title">Call Emergency Services 911</div>
                  <a
                    href="tel:911"
                    className="accident-call accident-call--emergency"
                    onClick={handleCall911}
                  >
                    <PhoneIcon color="#ffffff" size={18} />
                    Call 911
                  </a>
                </div>
              </div>

              <div className="accident-step">
                <div className="accident-step__number">2</div>
                <div className="accident-step__body">
                  <div className="accident-step__title">Call Accident Hotline</div>
                  <div className="accident-step__subtitle">(877) 459-3604</div>
                  <a
                    href="tel:877-459-3604"
                    className="accident-call"
                    onClick={handleCallHotline}
                  >
                    <PhoneIcon color="#ffffff" size={18} />
                    Call Accident Hotline
                  </a>
                </div>
              </div>

              <div className="accident-step accident-step--plain">
                <div className="accident-step__number">3</div>
                <div className="accident-step__body">
                  <div className="accident-step__title">Send in a Macro 14</div>
                </div>
              </div>
            </section>

            <section className="safety-section">
              <h2 className="safety-section__title home-section-label">At the scene</h2>

              <div className="accident-step accident-step--plain">
                <div className="accident-step__number">4</div>
                <div className="accident-step__body">
                  <div className="accident-step__title">Take photographs</div>
                  <p className="accident-step__intro">Include the following:</p>
                  <ul className="accident-step__list">
                    {PHOTO_CHECKLIST.map(item => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="accident-step accident-step--plain">
                <div className="accident-step__number">5</div>
                <div className="accident-step__body">
                  <div className="accident-step__title">Get witness names and phone numbers</div>
                </div>
              </div>
            </section>

            <div className="advance-card accident-note-card">
              <p className="advance-card__body">
                If you are issued a traffic citation after an accident you must call it in
                immediately. Call the accident hotline back to provide details.
              </p>
            </div>

            <section className="safety-section">
              <h2 className="safety-section__title home-section-label">Important warnings</h2>
              <div className="accident-warning-list">
                {WARNINGS.map(warning => (
                  <div key={warning} className="accident-warning">
                    <span className="accident-warning__icon" aria-hidden="true">
                      !
                    </span>
                    <p>{warning}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AccidentInfo;
